import type { Key, ReactNode } from 'react';
import type { BandKey, DayType, DistributionMode, HourRange, SpotAxis } from '../types';
import type { BandRateColumn } from './BandRateTable';
import { isWeekdayAllowed, isoWeekdays } from './weekdays';

/**
 * The spot matrix of a distribution: one "Spots" column for `PERIOD_TOTAL`, one
 * column per applicable weekday for `WEEK_ALIGNED`. Shared so the catalog editor
 * and the plan's conformance report are the same grid by construction — only
 * their cell content differs (an input against a report cell).
 *
 * `axis` is the weekday, or `null` for the single period bucket, so a cell never
 * re-derives the mode it was built from.
 *
 * Cells are addressed by band key alone, never by the band itself, so these
 * columns fit any band set — a table over a richer band type accepts them.
 */
export function distributionColumns(
  mode: DistributionMode,
  render: (bandKey: BandKey, axis: SpotAxis) => ReactNode,
  cellClassName?: (bandKey: BandKey, axis: SpotAxis) => string | undefined,
): (dayType: DayType) => BandRateColumn<HourRange>[] {
  const column = (key: Key, header: ReactNode, axis: SpotAxis): BandRateColumn<HourRange> => ({
    key,
    header,
    render: (_band, _dayType, bandKey) => render(bandKey, axis),
    cellClassName: cellClassName && ((_band, _dayType, bandKey) => cellClassName(bandKey, axis)),
  });

  return (dayType) =>
    mode === 'PERIOD_TOTAL'
      ? [column('period', 'Spots', null)]
      : isoWeekdays
          .filter((weekday) => isWeekdayAllowed(dayType, weekday.value))
          .map((weekday) => column(weekday.value, weekday.shortLabel, weekday.value));
}
